<template>
  <div class="page-container billing-batches-page">
    <portal to="page-name">Lotes</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <h1 class="card-title">Lotes</h1>
      </div>
      <div class="card-body" v-if="!billingEnabled">
        <div class="empty mt-2">
          <div class="empty-icon"><fa-icon :icon="['fal', 'layer-group']" size="3x"/></div>
          <p class="empty-title h5">Faturamento de convênios desligado nesta filial</p>
          <p class="empty-subtitle">
            Ligue o novo faturamento em Configurações para montar lotes, gerar o XML TISS
            e acompanhar glosas e repasses.
          </p>
          <router-link to="/financial/billing-batches/settings" class="btn btn-primary"
                       v-if="canManageSettings">Abrir configurações</router-link>
        </div>
      </div>
      <div class="card-body batches-starting" v-if="starting">
        <div class="loading loading-lg"></div>
      </div>
      <div class="card-body" v-show="!starting">
        <div class="batches-top">
          <div class="batches-top-fields">
            <plan-selector
              :key="planSelectorKey"
              :value="filter.planId"
              :plans="plans"
              :validator="$v.filter.planId"
              :disabled="loadingPlans"
              @input="onPlanChange"
            />
            <competence-picker v-model="filter.competence" :disabled="!filter.planId"/>
          </div>
          <template v-if="planFacts">
            <div class="divider" />
            <plan-facts :facts="planFacts"/>
          </template>
        </div>

        <div class="empty mt-2" v-if="!filter.planId">
          <div class="empty-icon"><fa-icon :icon="['fal', 'file-invoice']" size="3x"/></div>
          <p class="empty-title h5">Selecione o convênio</p>
          <p class="empty-subtitle">
            Os lotes são sempre de um único convênio e plano. Escolha acima para ver as guias
            abertas e os lotes da competência.
          </p>
        </div>
        <template v-else>
          <batch-kpis
            :totals="batches.totals"
            :by-status="batches.byStatus"
            :competence="filter.competence"
            :loading="loadingBatches"
          />

          <div class="batches-work" :class="{'batches-work-single': selected.length === 0}">
            <candidates-panel
              ref="candidates"
              :key="filter.planId"
              :plan-id="filter.planId"
              :insurance-id="insuranceId"
              :selected.sync="selected"
              :initial-filter="candidatesInitialFilter"
              :allow-mixed="allowMixed"
              @loaded="onCandidatesLoaded"
              @open-guide="openGuide"
            />
            <batch-live-panel
              v-if="selected.length > 0"
              :guides="selected"
              :limit="limit"
              :forecast="paymentPreview"
              :rule="paymentRule"
              :allow-mixed="allowMixed"
              :plan-name="plan ? plan.name : ''"
              :type="selectedType"
              :can-create="canCreate"
              :auto-split.sync="autoSplit"
              :expected-payment-at.sync="expectedPaymentAt"
              :building="building"
              @build="openBuild"
              @clear="clearSelection"
            />
          </div>

          <div class="selection-bar" v-if="selected.length > 0">
            <span class="selection-text">
              <strong>{{ selected.length }}</strong>
              {{ selected.length === 1 ? 'guia selecionada' : 'guias selecionadas' }}
              · <span class="num">{{ selectedTotal | currency }}</span>
            </span>
            <span class="selection-actions">
              <button class="btn btn-sm btn-link" @click="clearSelection">
                Limpar seleção <kbd>Esc</kbd>
              </button>
              <button class="btn btn-sm btn-primary btn-icon btn-icon-left"
                      :disabled="!canBuild" @click="openBuild">
                <fa-icon :icon="['fal', 'layer-group']"/>
                {{ buildLabel }}
              </button>
            </span>
          </div>

          <div class="cr-panel batches-panel">
            <div class="cr-panel-head">
              <div class="cr-panel-title">
                <fa-icon class="cr-panel-icon" :icon="['fal', 'layer-group']"/>
                Lotes de {{ competenceLabel(filter.competence) }}
                <span class="label label-rounded" v-if="!loadingBatches">
                  {{ batches.total }}
                </span>
              </div>
              <div class="cr-panel-subtitle">
                Todos os lotes do plano fechados nesta competência
              </div>
            </div>
            <div class="cr-toolbar">
              <div class="cr-toolbar-field">
                <label class="form-label">Status</label>
                <st-multi-select v-model="batchFilter.statuses" :items="statusItems"
                                 value-key="value" label-key="label"/>
              </div>
              <div class="cr-toolbar-field cr-toolbar-grow">
                <label class="form-label">Pesquisar</label>
                <input type="text" class="form-input" v-model="batchFilter.search" maxlength="40"
                       placeholder="Nº do lote ou protocolo" @keypress.enter="reloadBatches">
              </div>
              <div class="cr-toolbar-actions">
                <button class="btn btn-secondary btn-icon btn-icon-left"
                        :class="{loading: loadingBatches}" :disabled="loadingBatches"
                        @click="reloadBatches">
                  <fa-icon :icon="['fal', 'search']"/>Pesquisar
                </button>
              </div>
            </div>
            <div class="loading loading-lg cr-panel-loading" v-if="loadingBatches"></div>
            <template v-else>
              <div class="empty-inline" v-if="batchesError">
                <div class="empty-icon">
                  <fa-icon :icon="['fal', 'exclamation-triangle']" size="2x"/>
                </div>
                <div class="empty-title">Não foi possível carregar os lotes.</div>
                <button class="btn btn-gray btn-sm" @click="reloadBatches">Tentar novamente</button>
              </div>
              <div class="empty-inline" v-else-if="batches.items.length === 0">
                <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="2x"/></div>
                <div class="empty-title">
                  Nenhum lote fechado em {{ competenceLabel(filter.competence) }} para este plano.
                </div>
                <button class="btn btn-link btn-sm" @click="previousCompetence">
                  Ver competência anterior
                </button>
              </div>
              <template v-else>
                <batches-table
                  :items="batches.items"
                  :tiss-enabled="tissEnabled"
                  :can-update="canUpdate"
                  :can-print="canPrint"
                  :can-xml="canXml"
                  :can-import="canImport"
                  :new-ids="newIds"
                  :busy-id="busy.id"
                  :busy-action="busy.action"
                  @detail="openDetail"
                  @xml="xml"
                  @cover="openCover"
                  @send="openSend"
                  @import="openImport"
                />
                <div class="text-center batches-load-more" v-if="batches.hasMore">
                  <button class="btn btn-primary" :class="{loading: loadingMoreBatches}"
                          :disabled="loadingMoreBatches" @click="loadMoreBatches">
                    Carregar mais
                  </button>
                </div>
              </template>
            </template>
          </div>
        </template>
      </div>
      <div class="card-footer" v-if="billingEnabled && filter.planId && batches.items.length > 0">
        Lotes: <strong class="text-primary">{{ batches.totals.count || 0 }}</strong>
        · Guias: <strong class="text-primary">{{ batches.totals.guides || 0 }}</strong>
        · Faturado: <strong class="text-primary">{{ batches.totals.informed | currency }}</strong>
        · Liberado: <strong class="text-primary">{{ batches.totals.released | currency }}</strong>
        · Recebido: <strong class="text-primary">{{ batches.totals.paid | currency }}</strong>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <build-batch-modal
      v-if="buildModal.show"
      :show="buildModal.show"
      :plan="plan"
      :insurance-id="insuranceId"
      :type="selectedType"
      :guides="selected"
      :limit="limit"
      :forecast="paymentPreview"
      :rule="paymentRule"
      :auto-split="autoSplit"
      :expected-payment-at="expectedPaymentAt"
      @close="closeBuild"
      @built="onBuilt"
      @remove-guides="removeFromSelection"
    />
    <send-batch-modal
      v-if="sendModal.show"
      :show="sendModal.show"
      :batch="sendModal.batch"
      @close="closeSend"
    />
    <cover-print-modal
      v-if="coverModal.show"
      :show="coverModal.show"
      :batch="coverModal.batch"
      @close="coverModal.show = false"
    />
    <xml-errors-modal
      v-if="xmlErrorsModal.show"
      :show="xmlErrorsModal.show"
      :batch="xmlErrorsModal.batch"
      :errors="xmlErrorsModal.errors"
      :warnings="xmlErrorsModal.warnings"
      @close="closeXmlErrors"
      @retry="retryXml"
      @open-guide="openGuideById"
    />
    <guide-modal
      v-if="guideModal.show"
      :show="guideModal.show"
      :account-id="guideModal.accountId"
      @close="closeGuide"
      @deleted="closeGuide(true)"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import {
  LIST_BILLINGS,
  CREATE_BILLING,
  UPDATE_BILLING,
  PRINT_BILLING,
  GENERATE_BILLING_XML,
} from '@/data/actions/modules/financial';
import { UPDATE_BRANCH } from '@/data/actions/modules/general';
import { statuses as batchStatuses } from '@/data/billing-batch-statuses';
import Forbidden from '@/components/auth/Forbidden.vue';
import {
  IMPORT_BILLING_STATEMENTS,
  competenceLabel,
  competenceParams,
  sumBy,
  batchRoute,
  batchTypeOf,
  paymentRuleLabel,
} from '../service';
import xmlMixin from '../mixins/xml';
import { GuideModal } from './glossTabs';
import PlanSelector from './PlanSelector.vue';
import PlanFacts from './PlanFacts.vue';
import CompetencePicker from './CompetencePicker.vue';
import BatchKpis from './BatchKpis.vue';
import CandidatesPanel from './CandidatesPanel.vue';
import BatchLivePanel from './BatchLivePanel.vue';
import BatchesTable from './BatchesTable.vue';
import BuildBatchModal from '../modals/BuildBatch.vue';
import SendBatchModal from '../modals/SendBatch.vue';
import CoverPrintModal from '../modals/CoverPrint.vue';
import XmlErrorsModal from '../modals/XmlErrors.vue';

const DEFAULT_LIMIT = 100;

function blankBatches() {
  return {
    items: [],
    total: 0,
    hasMore: false,
    offset: 0,
    limit: 50,
    totals: {},
    byStatus: {},
  };
}

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value).split(',').map(item => item.trim()).filter(Boolean);
}

export default {
  mixins: [xmlMixin],
  components: {
    Forbidden,
    PlanSelector,
    PlanFacts,
    CompetencePicker,
    BatchKpis,
    CandidatesPanel,
    BatchLivePanel,
    BatchesTable,
    BuildBatchModal,
    SendBatchModal,
    CoverPrintModal,
    XmlErrorsModal,
    GuideModal,
  },
  data() {
    return {
      guideModal: { show: false, accountId: null },
      starting: true,
      candidatesReady: false,
      loadingPlans: false,
      plans: [],
      planSelectorKey: 0,
      planFacts: null,
      filter: {
        planId: '',
        competence: moment().format('YYYY-MM'),
      },
      candidatesInitialFilter: null,
      preselectIds: [],
      selected: [],
      autoSplit: true,
      expectedPaymentAt: null,
      building: false,
      loadingBatches: false,
      loadingMoreBatches: false,
      batchesError: false,
      batches: blankBatches(),
      batchFilter: {
        statuses: Object.keys(batchStatuses).filter(status => status !== 'canceled'),
        search: '',
      },
      newIds: [],
      busy: { id: null, action: null },
      buildModal: { show: false },
      sendModal: { show: false, batch: null },
      coverModal: { show: false, batch: null },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    branchSettings() {
      return (this.user && this.user.branch && this.user.branch.settings) || {};
    },
    billingEnabled() {
      const { insuranceBilling } = this.branchSettings;
      return !!(insuranceBilling && insuranceBilling.enabled);
    },
    hasAccess() {
      return this.$can(LIST_BILLINGS);
    },
    canCreate() {
      return this.$can(CREATE_BILLING);
    },
    canUpdate() {
      return this.$can(UPDATE_BILLING);
    },
    canPrint() {
      return this.$can(PRINT_BILLING);
    },
    canXml() {
      return this.$can(GENERATE_BILLING_XML);
    },
    canImport() {
      return this.$can(IMPORT_BILLING_STATEMENTS);
    },
    canManageSettings() {
      return this.$can(UPDATE_BRANCH);
    },
    plan() {
      return this.plans.find(({ id }) => id === this.filter.planId) || null;
    },
    insuranceId() {
      return this.plan ? this.plan.insuranceId : null;
    },
    limit() {
      return (this.planFacts && this.planFacts.limit) || DEFAULT_LIMIT;
    },
    paymentRule() {
      return this.planFacts ? this.planFacts.paymentRule : null;
    },
    paymentPreview() {
      return this.planFacts ? this.planFacts.paymentPreview : null;
    },
    allowMixed() {
      return !!(this.planFacts && this.planFacts.allowMixed);
    },
    tissEnabled() {
      return !this.planFacts || !this.planFacts.tiss || this.planFacts.tiss.enabled !== false;
    },
    selectedType() {
      return batchTypeOf(this.selected);
    },
    selectedTotal() {
      return sumBy(this.selected, guide => guide.totals && guide.totals.informed);
    },
    batchCount() {
      return Math.ceil(this.selected.length / this.limit);
    },
    canBuild() {
      return this.canCreate && (this.selected.length <= this.limit || this.autoSplit);
    },
    buildLabel() {
      return this.selected.length > this.limit && this.autoSplit
        ? `Montar ${this.batchCount} lotes`
        : 'Montar lote';
    },
    statusItems() {
      return Object.keys(batchStatuses).map(value => ({ value, label: batchStatuses[value] }));
    },
  },
  validations() {
    return {
      filter: {
        planId: { required },
      },
    };
  },
  watch: {
    'filter.competence': function onCompetenceChange() {
      if (this.filter.planId) this.reloadBatches();
    },
    selected(guides) {
      if (guides.length === 0) this.expectedPaymentAt = null;
    },
  },
  async mounted() {
    if (!this.hasAccess || !this.billingEnabled) {
      this.starting = false;
      return;
    }
    document.addEventListener('keydown', this.onKeydown);
    this.readQuery();
    await this.loadPlans();
    if (!this.filter.planId) this.candidatesReady = true;
    this.releaseStart();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    competenceLabel,
    readQuery() {
      const { query } = this.$route;
      this.preselectIds = toArray(query.accountIds);
      if (query.planId) {
        this.filter.planId = String(query.planId);
      }
      if (moment(query.competence, 'YYYY-MM', true).isValid()) {
        this.filter.competence = String(query.competence);
      }
      if (this.preselectIds.length > 0) {
        const endDate = moment().endOf('month');
        this.candidatesInitialFilter = {
          startDate: endDate.clone().subtract(3, 'months').add(1, 'day').format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        };
      }
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const modalOpen = this.buildModal.show || this.sendModal.show || this.guideModal.show
        || this.coverModal.show || this.xmlErrorsModal.show;
      if (!modalOpen && this.selected.length > 0) this.clearSelection();
    },
    loadPlans() {
      this.loadingPlans = true;
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.plans = data.items
            .filter(item => item.type !== 'particular')
            .map(item => ({
              id: item.plan.id,
              insuranceId: item.id,
              type: item.type,
              name: item.customName,
            }));
          if (this.filter.planId && !this.plan) {
            this.filter.planId = '';
            this.$toast.error('O plano informado não está disponível para faturamento.');
          }
          if (this.filter.planId) return this.reloadBatches();
          return null;
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loadingPlans = false;
        });
    },
    onPlanChange(planId) {
      if (planId === this.filter.planId) return;
      if (this.selected.length === 0) {
        this.applyPlan(planId);
        return;
      }
      const count = this.selected.length;
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Você tem ${count} ${count === 1 ? 'guia selecionada' : 'guias selecionadas'}. `
          + 'Trocar o convênio limpa a seleção.</div></div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
            click: (close) => {
              close();
              this.planSelectorKey += 1;
            },
          },
          {
            label: 'Sim',
            classes: 'btn-primary ml-2',
            click: (close) => {
              close();
              this.applyPlan(planId);
            },
          },
        ],
      });
    },
    applyPlan(planId) {
      this.selected = [];
      this.planFacts = null;
      this.batches = blankBatches();
      this.batchesError = false;
      this.filter.planId = planId;
      this.$v.filter.planId.$touch();
      if (planId) this.reloadBatches();
    },
    releaseStart() {
      if (this.loadingPlans || this.loadingBatches || !this.candidatesReady) return;
      this.starting = false;
    },
    onCandidatesLoaded({ plan }) {
      this.candidatesReady = true;
      if (plan) {
        this.planFacts = {
          limit: plan.limit || DEFAULT_LIMIT,
          paymentRule: plan.paymentRule || null,
          paymentPreview: plan.paymentPreview || null,
          paymentLabel: paymentRuleLabel(plan.paymentRule),
          allowMixed: !!plan.allowMixedBatches,
          tiss: plan.tiss || { enabled: false, version: null },
        };
      }
      if (this.preselectIds.length > 0) {
        const ids = this.preselectIds;
        this.preselectIds = [];
        const found = this.$refs.candidates.select(ids);
        if (found < ids.length) {
          const missing = ids.length - found;
          this.$toast.error(missing === 1
            ? 'Uma guia informada não está mais disponível para lote.'
            : `${missing} guias informadas não estão mais disponíveis para lote.`);
        }
      }
      this.releaseStart();
    },
    clearSelection() {
      this.selected = [];
    },
    removeFromSelection(ids) {
      this.selected = this.selected.filter(({ id }) => !ids.includes(id));
      if (this.selected.length === 0) this.buildModal.show = false;
    },
    openGuide(guide) {
      this.openGuideById(guide.id);
    },
    openGuideById(id) {
      this.guideModal.accountId = id;
      this.guideModal.show = true;
    },
    closeGuide(saved) {
      this.guideModal.show = false;
      this.guideModal.accountId = null;
      if (saved && this.$refs.candidates) this.$refs.candidates.reload();
    },
    openDetail(batch) {
      this.$router.push(batchRoute(batch.id));
    },
    previousCompetence() {
      this.filter.competence = moment(this.filter.competence, 'YYYY-MM')
        .subtract(1, 'month').format('YYYY-MM');
    },
    reloadBatches() {
      this.batches.offset = 0;
      return this.loadBatches();
    },
    loadMoreBatches() {
      this.batches.offset += this.batches.limit;
      return this.loadBatches();
    },
    loadBatches() {
      if (!this.filter.planId) return Promise.resolve();
      const firstData = this.batches.offset === 0;
      if (firstData) this.loadingBatches = true; else this.loadingMoreBatches = true;
      this.batchesError = false;

      const params = {
        planId: this.filter.planId,
        limit: this.batches.limit,
        offset: this.batches.offset,
        ...competenceParams(this.filter.competence),
      };
      if (this.insuranceId) params.insuranceId = this.insuranceId;
      if (this.batchFilter.statuses.length > 0) params.status = this.batchFilter.statuses.join(',');
      if (this.batchFilter.search.trim()) params.search = this.batchFilter.search.trim();

      return this.$http.get('/billing-batches', { params })
        .then(({ data }) => {
          const items = firstData ? data.items : [...this.batches.items, ...data.items];
          this.batches.items = items;
          this.batches.total = data.total;
          this.batches.hasMore = items.length < data.total;
          this.batches.totals = data.totals || {};
          this.batches.byStatus = data.byStatus || {};
        })
        .catch((e) => {
          this.batchesError = true;
          this.$toast.error(e);
        })
        .finally(() => {
          this.loadingBatches = false;
          this.loadingMoreBatches = false;
          this.releaseStart();
        });
    },
    openBuild() {
      if (!this.canBuild || this.selected.length === 0) return;
      this.buildModal.show = true;
    },
    closeBuild() {
      this.buildModal.show = false;
    },
    onBuilt({ batches, removedIds }) {
      this.buildModal.show = false;
      const ids = this.selected.map(({ id }) => id);
      if (this.$refs.candidates) {
        this.$refs.candidates.removeItems(removedIds || ids);
      }
      this.selected = [];
      this.newIds = batches.map(({ id }) => id);
      const closedAt = batches[0] && batches[0].dates && batches[0].dates.closedAt;
      const competence = closedAt ? moment(closedAt).format('YYYY-MM') : this.filter.competence;
      if (competence !== this.filter.competence) {
        this.filter.competence = competence;
      } else {
        this.reloadBatches();
      }
      setTimeout(() => {
        this.newIds = [];
      }, 3500);
    },
    async xml(batch) {
      this.busy = { id: batch.id, action: 'xml' };
      try {
        const done = await this.downloadXml(batch);
        if (done && batch.status === 'closed') this.reloadBatches();
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.busy = { id: null, action: null };
      }
    },
    retryXml() {
      const { batch } = this.xmlErrorsModal;
      this.closeXmlErrors();
      if (batch) this.xml(batch);
    },
    openCover(batch) {
      this.coverModal.batch = batch;
      this.coverModal.show = true;
    },
    openSend(batch) {
      this.sendModal.batch = batch;
      this.sendModal.show = true;
    },
    closeSend(sent) {
      this.sendModal.show = false;
      this.sendModal.batch = null;
      if (sent) this.reloadBatches();
    },
    openImport(batch) {
      this.$router.push({ ...batchRoute(batch.id), query: { tab: 'statements', import: '1' } });
    },
  },
};
</script>

<style lang="scss">
@import "../batch.scss";

.billing-batches-page {
  .batches-starting {
    padding-bottom: $layout-spacing-xl;
    padding-top: $layout-spacing-xl;
  }
  .batches-top {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    padding: $layout-spacing $layout-spacing-lg;
    .batches-top-fields {
      align-items: flex-end;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing $layout-spacing-lg;
    }
    .divider {
      border-color: $border-color-dark;
      margin: $layout-spacing 0;
    }
  }
  .batches-work {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: minmax(0, 1fr) 19rem;
    margin-top: $layout-spacing-lg;
    &.batches-work-single {
      grid-template-columns: minmax(0, 1fr);
    }
    .cr-panel {
      margin-top: 0;
    }
  }
  .batches-load-more {
    border-top: $border-color solid $border-width;
    padding: $layout-spacing;
  }
  .card-header .btn-link {
    margin-top: .1rem;
  }
  @media screen and (max-width: $size-md) {
    .batches-work {
      grid-template-columns: 1fr;
      .batch-live {
        order: -1;
      }
    }
  }
}
</style>
